import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, saveAuthTokens, clearAuthTokens, hydrateAuthTokensFromNative } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Native APK: copy tokens out of Capacitor Preferences into
      // localStorage so the axios interceptor's Authorization header
      // is populated BEFORE /auth/me fires. Without this, the very
      // first request after a fresh launch goes out cookie-less +
      // token-less → 401 → user sees the login screen even though
      // they're "really" still authenticated.
      // Race the native hydrate against a 1.5s deadline — on some
      // Android APKs the dynamic import of @capacitor/preferences can
      // stall on cold boot and would otherwise block /auth/me forever.
      await Promise.race([
        hydrateAuthTokensFromNative(),
        new Promise((res) => setTimeout(res, 1500)),
      ]);
      // Hard timeout on /auth/me so the boot path can never hang.
      const { data } = await api.get("/auth/me", { timeout: 5000 });
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If OAuth callback in URL, skip /me check; let AuthCallback set session.
    // Covers both the legacy hash-based flow (#session_id=…) AND the
    // current direct code flow (/auth/google?code=…). Without this
    // skip, checkAuth's /auth/me racing against AuthCallback's code
    // exchange can 401 AFTER a successful sign-in and blow away
    // setUser(data) — user lands on /dashboard, guard sees user=null,
    // bounces to /login, feels like a Google loop.
    if (typeof window !== "undefined") {
      const path = window.location.pathname || "";
      const hash = window.location.hash || "";
      const search = window.location.search || "";
      const isLegacyOAuth = hash.includes("session_id=");
      const isDirectOAuth =
        (path === "/auth/google" || path === "/auth/callback") &&
        (search.includes("code=") || hash.includes("session_id="));
      if (isLegacyOAuth || isDirectOAuth) {
        setLoading(false);
        return;
      }
    }
    checkAuth();
    // Safety net: on Capacitor APKs the /auth/me request occasionally
    // hangs (cold network, dropped cookies, dynamic-import stalls on
    // @capacitor/preferences). Without this, `loading` would stay true
    // forever — and because HomeRouter / ProtectedRoute now render `null`
    // while loading, the user would be stuck staring at the blue body
    // background instead of reaching /login.
    // 4-second cap unblocks the UI; if the user is actually authed,
    // /auth/me will eventually resolve and `setUser(...)` will then
    // refresh the tree into /dashboard automatically.
    const failsafe = setTimeout(() => setLoading(false), 2500);
    return () => clearTimeout(failsafe);
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Persist tokens for native/mobile auth — backend now returns them
    // alongside the public_user payload so Capacitor WebViews don't lose
    // the session when SameSite=None cookies get dropped between launches.
    saveAuthTokens(data);
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    saveAuthTokens(data);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    clearAuthTokens();
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      // Don't proactively wipe the user on a transient 401 — the axios
      // interceptor already tries /auth/refresh. Only logout() (which
      // clears tokens) should drop the user object. This stops the
      // "I closed the app and came back logged out" complaint.
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, logout, refreshUser, setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};
